<script setup lang="ts">
import { computed, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    CheckCircleIcon,
    InformationCircleIcon,
    MinusCircleIcon,
    XIcon,
} from '@heroicons/vue/solid';
import { MetaPhoneNumber, MetaWhatsAppNumberPolicyPhone } from '@/types';
import PhoneNumberEligibilityState from './PhoneNumberEligibilityState.vue';
import { isPhoneEnabled } from '@/utils/whatsappMeta';

const props = defineProps({
    phone: { type: Object as PropType<MetaPhoneNumber>, required: true },
    policyPhone: { type: Object as PropType<MetaWhatsAppNumberPolicyPhone | null>, default: null },
    canEnable: { type: Boolean, default: false },
});

defineEmits<{
    activate: [phoneNumberId: string];
    approval: [phone: MetaWhatsAppNumberPolicyPhone, enabled: boolean];
}>();

const { locale, t } = useI18n();
const detailsOpen = ref(false);

const isEnabled = computed(() => isPhoneEnabled(props.policyPhone));
const axes = computed(() => [
    {
        owner: t('whatsappMeta.phoneAxis.meta'),
        ok: props.policyPhone?.isActive === true,
        value: t(
            props.policyPhone?.isActive
                ? 'whatsappMeta.phoneAxis.registered'
                : 'whatsappMeta.phoneAxis.notRegistered',
        ),
    },
    {
        owner: t('whatsappMeta.phoneAxis.mobcall'),
        ok: isEnabled.value,
        value: t(
            isEnabled.value
                ? 'whatsappMeta.phoneAxis.enabled'
                : 'whatsappMeta.phoneAxis.notEnabled',
        ),
    },
]);
const canActivate = computed(() => Boolean(props.policyPhone && !props.policyPhone.isActive));
const canEnablePhone = computed(() =>
    Boolean(props.policyPhone?.isActive && !isEnabled.value && props.canEnable),
);
const canDisable = computed(() => Boolean(props.policyPhone?.isActive && isEnabled.value));
const detailsId = computed(() => `phone-details-${props.phone.phoneNumberId}`);
const lastSyncedAt = computed(() => {
    if (!props.phone.lastSyncedAt) return t('whatsappMeta.phone.notSynchronized');
    return new Intl.DateTimeFormat(locale.value, {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(new Date(props.phone.lastSyncedAt));
});
</script>

<template>
    <div
        class="flex flex-col justify-between gap-3 p-4 hover:bg-gray-50/40 sm:flex-row sm:items-center"
    >
        <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
                <span class="text-sm font-semibold text-gray-900">
                    {{
                        phone.displayPhoneNumber ||
                        phone.phoneNumberId ||
                        t('whatsappMeta.phone.unidentifiedNumber')
                    }}
                </span>
                <PhoneNumberEligibilityState :phone="policyPhone" />
                <button
                    type="button"
                    class="focus-visible:ring-primary inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2"
                    :aria-expanded="detailsOpen"
                    :aria-controls="detailsId"
                    :aria-label="t('whatsappMeta.detail.showTechnicalDetails')"
                    @click="detailsOpen = !detailsOpen"
                >
                    <InformationCircleIcon class="h-4 w-4" aria-hidden="true" />
                </button>
            </div>

            <p v-if="policyPhone" class="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                <span v-for="axis in axes" :key="axis.owner" class="flex items-center">
                    <CheckCircleIcon
                        v-if="axis.ok"
                        class="mr-1 h-4 w-4 text-emerald-500"
                        aria-hidden="true"
                    />
                    <MinusCircleIcon v-else class="mr-1 h-4 w-4 text-gray-400" aria-hidden="true" />
                    <span class="mr-1 font-medium text-gray-700">{{ axis.owner }}:</span>
                    {{ axis.value }}
                </span>
            </p>

            <div
                v-if="detailsOpen"
                :id="detailsId"
                class="mt-2.5 space-y-1 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600"
            >
                <div class="flex items-center justify-between">
                    <span class="font-medium text-gray-700">
                        {{ t('whatsappMeta.detail.technicalDetails') }}
                    </span>
                    <button
                        type="button"
                        class="text-gray-400 hover:text-gray-600"
                        :aria-label="t('whatsappMeta.detail.closeTechnicalDetails')"
                        @click="detailsOpen = false"
                    >
                        <XIcon class="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                </div>
                <p>
                    {{ t('whatsappMeta.detail.verifiedName') }}:
                    {{ phone.verifiedName || t('whatsappMeta.phone.noVerifiedName') }}
                </p>
                <p>
                    {{ t('whatsappMeta.detail.metaPhoneId') }}:
                    {{ phone.phoneNumberId || t('whatsappMeta.phone.idUnavailable') }}
                </p>
                <p>
                    {{ t('whatsappMeta.detail.registrationStatus') }}:
                    {{ policyPhone?.registrationStatus || phone.registrationStatus }}
                </p>
                <p>{{ t('whatsappMeta.detail.lastSyncedAt') }}: {{ lastSyncedAt }}</p>
            </div>
        </div>

        <div class="flex shrink-0 items-center gap-3">
            <UiButton
                v-if="canActivate && phone.phoneNumberId"
                variant="white"
                text-variant="primary"
                @click="$emit('activate', phone.phoneNumberId)"
            >
                {{ t('whatsappMeta.phone.activateOnMeta') }}
            </UiButton>
            <UiButton
                v-else-if="canEnablePhone && policyPhone"
                variant="white"
                text-variant="primary"
                @click="$emit('approval', policyPhone, true)"
            >
                {{ t('whatsappMeta.phone.enable') }}
            </UiButton>
            <UiButton
                v-else-if="canDisable && policyPhone"
                variant="white"
                text-variant="red-600"
                @click="$emit('approval', policyPhone, false)"
            >
                {{ t('whatsappMeta.phone.disable') }}
            </UiButton>
        </div>
    </div>
</template>
